// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Card, CardContent, Typography } from '@mui/material';
import { Table } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import {
  useServiceCommissionsCard,
  type ServiceCommissionsCardProps,
} from './use-service-commissions-card';

export const ServiceCommissionsCard = defineComponent<
  'ServiceCommissionsCard',
  ServiceCommissionsCardProps
>({
  displayName: 'ServiceCommissionsCard',
  useSetup: useServiceCommissionsCard,
  View: ({ advisoryTypeKey, commissionManagedAssets, hasRetrocessions }) => {
    return (
      <>
        <Card
          variant="outlined"
          className="!mb-6 !rounded-lg"
          data-testid="service-type-card"
        >
          <CardContent className="!p-7">
            <Typography
              variant="h4"
              className="!mb-6"
              data-testid="service-type-title"
            >
              <Translation
                i18nKey="onboarding-process-overview.result-and-signature.service-and-commissions.service-type.title"
                ns="components"
              />
            </Typography>
            <div className="!flex !flex-col !gap-2 !rounded-lg !bg-section !p-6">
              <Typography
                variant="body1"
                className="text-base !font-semibold"
                data-testid="service-type-value"
              >
                <Translation
                  i18nKey={`activation-step2.advisory-types.${advisoryTypeKey}.name`}
                  ns="components"
                />
              </Typography>
            </div>
          </CardContent>
        </Card>

        <Card
          variant="outlined"
          className="!mb-6 !rounded-lg"
          data-testid="commissions-card"
        >
          <CardContent className="!p-7">
            <Typography
              variant="h4"
              className="!mb-6"
              data-testid="commissions-title"
            >
              <Translation
                i18nKey="onboarding-process-overview.result-and-signature.service-and-commissions.commissions.title"
                ns="components"
              />
            </Typography>

            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div
                className="!flex !flex-col !gap-2 !rounded-lg !bg-section !p-6"
                data-testid="management-commission-card"
              >
                <Typography
                  variant="body1"
                  className="text-base !font-semibold"
                  data-testid="management-commission-label"
                >
                  <Translation
                    i18nKey="onboarding-process-overview.result-and-signature.service-and-commissions.commissions.management-commission.label"
                    ns="components"
                  />
                </Typography>
                <Typography
                  variant="body2"
                  className="text-base !font-semibold"
                  data-testid="management-commission-value"
                >
                  <Table.Data
                    format="percentage"
                    value={commissionManagedAssets}
                    formatOptions={{
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }}
                  />
                </Typography>
                <Typography
                  variant="caption"
                  className="text-sm !text-gray-500"
                  data-testid="management-commission-explanation"
                >
                  <Translation
                    i18nKey="onboarding-process-overview.result-and-signature.service-and-commissions.commissions.management-commission.annual"
                    ns="components"
                  />
                </Typography>
              </div>

              <div
                className="!flex !flex-col !gap-2 !rounded-lg !bg-section !p-6"
                data-testid="retrocessions-card"
              >
                <Typography
                  variant="body1"
                  className="text-base !font-semibold"
                  data-testid="retrocessions-label"
                >
                  <Translation
                    i18nKey="onboarding-process-overview.result-and-signature.service-and-commissions.commissions.retrocessions.label"
                    ns="components"
                  />
                </Typography>
                <Typography
                  variant="body2"
                  className="text-base !font-semibold"
                  data-testid="retrocessions-value"
                >
                  <Translation
                    i18nKey={`activation-step3.retrocession-types.${
                      hasRetrocessions ? 'yes' : 'no'
                    }.name`}
                    ns="components"
                  />
                </Typography>
                {hasRetrocessions && (
                  <Typography
                    variant="caption"
                    className="text-sm !text-gray-500"
                    data-testid="retrocessions-explanation"
                  >
                    <Translation
                      i18nKey="contract-review.retrocessions.explanation"
                      ns="components"
                    />
                  </Typography>
                )}
              </div>
            </div>

            <Typography
              variant="body2"
              color="text.secondary"
              className="text-justify text-base leading-6"
              data-testid="costs-note"
            >
              <Translation
                i18nKey="onboarding-process-overview.result-and-signature.service-and-commissions.commissions.costs-note"
                ns="components"
              />
            </Typography>
          </CardContent>
        </Card>
      </>
    );
  },
});
